import moment from "moment";

// ReactElement에서 텍스트만 재귀 추출
const extractTextFromElement = (node: any): string => {
  if (node == null) return "";
  if (typeof node === "string" || typeof node === "number") return String(node);
  if (Array.isArray(node)) {
    for (const child of node) {
      const text = extractTextFromElement(child);
      if (text) return text;
    }
    return "";
  }
  if (typeof node === "object" && "props" in node) {
    return extractTextFromElement(node.props.children);
  }
  return "";
};

// 엑셀 컬럼에서 Header 텍스트 추출 (함수형/ReactElement 대응)
const getColumnHeaderText = (column: any): string => {
  if (typeof column.Header === "function") {
    const headerElement = column.Header();
    return extractTextFromElement(headerElement) || column.accessor || "";
  }

  return String(column.Header);
};

// 제외할 accessor 배열로 컬럼 필터링
const filterColumnsForExcel = (columns: any[], excludeAccessors?: string[]): any[] => {
  if (!excludeAccessors || excludeAccessors.length === 0) {
    return columns;
  }
  return columns.filter((column: any) => !excludeAccessors.includes(column.accessor));
};

// 엑셀 다운로드
const downloadExcel = async <T>({ data, fileName, header }: { data: T[]; fileName: string; header: string[] }) => {
  const XLSX = await import("xlsx");

  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.json_to_sheet([]);

  XLSX.utils.sheet_add_aoa(ws, [header], { origin: "A1" });
  XLSX.utils.sheet_add_json(ws, data, { origin: "A2", skipHeader: true });
  XLSX.utils.book_append_sheet(wb, ws, "Sheet1");
  XLSX.writeFile(wb, `${fileName}.xlsx`);

  return true;
};

// react-table 컬럼 정의와 원본 데이터를 받아 엑셀용 데이터로 변환
export const mapDataToExcelByColumns = <T>(rows: T[], columns: any[], excludeAccessors?: string[]): any[] => {
  // 제외할 accessor가 있으면 필터링
  const filteredColumns = excludeAccessors ? columns.filter((column: any) => !excludeAccessors.includes(column.accessor)) : columns;

  return rows.map((rowData) => {
    const convertedRow: Record<string, any> = {};

    filteredColumns.forEach((column: any) => {
      const accessor = column.accessor as string;
      const headerText = getColumnHeaderText(column);

      let value: any;

      // 컬럼에 excelValue가 정의되어 있으면 우선 사용 (UI 표시 텍스트와 1:1 매칭용)
      if (typeof column.excelValue === "function") {
        value = column.excelValue(rowData);
      } else {
        const rawValue = (rowData as any)?.[accessor];
        value = rawValue;
      }

      convertedRow[headerText] = value ? value : "-";
    });

    return convertedRow;
  });
};

interface ExcelDownloadParams<T> {
  /**
   * 엑셀에 출력할 데이터 배열
   */
  data: T[];
  /**
   * 테이블 컬럼 정의 배열
   */
  columns: any[];
  /**
   * 엑셀 파일명 prefix (예: "신청/계약-전체")
   */
  fileNamePrefix: string;
  /**
   * 엑셀 다운로드에서 제외할 컬럼 accessor 배열
   */
  excludeAccessors?: string[];
  /**
   * 데이터가 없을 때 호출할 콜백
   */
  onNoData?: () => void;
  /**
   * 다운로드 성공 시 호출할 콜백
   */
  onSuccess?: () => void;
  /**
   * 다운로드 실패 시 호출할 콜백
   */
  onError?: (error: any) => void;
}

/**
 * 엑셀 다운로드 함수
 */
export const downloadExcelByColumns = async <T>(params: ExcelDownloadParams<T>) => {
  const { data, columns, fileNamePrefix, excludeAccessors = [], onNoData, onSuccess, onError } = params;

  try {
    if (!data || data.length === 0) {
      onNoData?.();
      return;
    }

    const mappedData = mapDataToExcelByColumns<T>(data, columns, excludeAccessors);
    const filteredColumns = filterColumnsForExcel(columns, excludeAccessors);

    const fileName = `ctrl.room-${fileNamePrefix}_${moment().format("YYYYMMDDHHmmss")}`;

    await downloadExcel({
      data: mappedData,
      fileName,
      header: filteredColumns.map((column) => getColumnHeaderText(column)),
    });

    onSuccess?.();
  } catch (error) {
    onError?.(error);
  }
};

// export for backward compatibility
export { getColumnHeaderText, filterColumnsForExcel, downloadExcel };
